import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { MultiSelect } from '../ui/MultiSelect/MultiSelect';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RolesService } from '@/services/RolesService';
import { useNotificationStore } from '@/stores/notificationStore';
import { Spinner } from '../ui/Spinner/Spinner';

interface EditMemberRolesModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  userId: string;
  userEmail: string;
  userName?: string;
}

export function EditMemberRolesModal({
  isOpen,
  onClose,
  orgId,
  userId,
  userEmail,
  userName,
}: EditMemberRolesModalProps) {
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const notificationStore = useNotificationStore();
  const rolesService = new RolesService();

  // Fetch available roles
  const { data: availableRoles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', orgId],
    queryFn: () => rolesService.getRoles(),
    enabled: isOpen && !!orgId,
  });

  // Fetch user's current roles
  const { data: userPermissionsData, isLoading: userRolesLoading } = useQuery({
    queryKey: ['userPermissions', orgId, userId],
    queryFn: () => rolesService.getUserPermissions(orgId, userId),
    enabled: isOpen && !!orgId && !!userId,
  });

  // Update local state when user's roles are loaded
  useEffect(() => {
    if (userPermissionsData?.roles) {
      const currentRoleIds = userPermissionsData.roles.map((role: any) => role.id);
      setSelectedRoleIds(currentRoleIds);
    }
  }, [userPermissionsData]);

  // Mutation to update roles
  const updateRolesMutation = useMutation({
    mutationFn: async (roleIds: string[]) => {
      await rolesService.assignRolesToUser(orgId, userId, roleIds);
    },
    onSuccess: () => {
      notificationStore.success('Roles updated successfully');
      // Invalidate the org members list to show updated roles
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
      // Invalidate user permissions to refresh the modal data if reopened
      queryClient.invalidateQueries({ queryKey: ['userPermissions', orgId, userId] });
      onClose();
    },
    onError: (error: any) => {
      notificationStore.error(error.message || 'Failed to update roles');
    },
  });

  const handleSave = () => {
    updateRolesMutation.mutate(selectedRoleIds);
  };

  const roleOptions = availableRoles.map((role) => ({
    value: role.id,
    label: role.name,
    description: role.description || undefined,
  }));

  const isLoading = rolesLoading || userRolesLoading;
  const displayName = userName || userEmail;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit Roles for ${displayName}`}
      size="md"
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Assigned Roles
              </label>
              <p className="text-sm text-muted-foreground">
                Select one or more roles to assign to this member. Each role contains a set of permissions.
              </p>
              <MultiSelect
                options={roleOptions}
                value={selectedRoleIds}
                onChange={setSelectedRoleIds}
                placeholder="Select roles..."
              />
            </div>

            {userPermissionsData?.roles && userPermissionsData.roles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">Current Roles & Permissions</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {userPermissionsData.roles.map((role: any) => (
                    <div
                      key={role.id}
                      className="border border-border rounded-lg p-3 bg-muted"
                    >
                      <div className="font-medium text-sm text-foreground">
                        {role.name}
                        {role.is_system && (
                          <span className="ml-2 text-xs text-muted-foreground">(System)</span>
                        )}
                      </div>
                      {role.permissions && role.permissions.length > 0 && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={updateRolesMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading || updateRolesMutation.isPending}
          loading={updateRolesMutation.isPending}
        >
          Save Changes
        </Button>
      </div>
    </Modal>
  );
}

